import { GroupElement, TemplateElement } from '../templateEditor.types'

type ZOrderMode = 'front' | 'back' | 'forward' | 'backward'

const topLevelOf = (elements: TemplateElement[]) => elements.filter((element) => !element.groupId)

const getGroup = (elements: TemplateElement[], groupId: string) =>
    elements.find((element) => element.id === groupId && element.type === 'group') as GroupElement | undefined

const indicesOf = (list: string[], ids: string[]) => ids.map((id) => list.indexOf(id)).filter((index) => index >= 0)

const withGroupChildren = (elements: TemplateElement[], groupId: string, children: string[]) => {
    const next = elements.map((element) =>
        element.id === groupId && element.type === 'group' ? { ...element, children } : element
    )
    return rebuildElementsByTopOrder(next, topLevelOf(next))
}

/** Собирает flat-массив: дети группы в порядке `group.children`, затем сама группа / top-level. */
export const rebuildElementsByTopOrder = (
    elements: TemplateElement[],
    topOrder: TemplateElement[]
): TemplateElement[] => {
    const byId = new Map(elements.map((element) => [element.id, element]))
    const result: TemplateElement[] = []

    for (const top of topOrder) {
        if (top.type === 'group') {
            const group = (byId.get(top.id) as GroupElement | undefined) ?? top
            for (const childId of group.children) {
                const child = byId.get(childId)
                if (child) {
                    result.push(child)
                }
            }
            result.push(group)
            continue
        }

        const current = byId.get(top.id)
        if (current) {
            result.push(current)
        }
    }

    return result
}

const moveIndices = (list: string[], indices: number[], mode: ZOrderMode): string[] => {
    if (indices.length === 0) {
        return list
    }

    if (mode === 'front' || mode === 'back') {
        const moving = indices.map((index) => list[index])
        const rest = list.filter((_, index) => !indices.includes(index))
        return mode === 'front' ? [...rest, ...moving] : [...moving, ...rest]
    }

    const next = [...list]
    const step = mode === 'forward' ? 1 : -1
    const ordered = [...indices].sort((a, b) => (step > 0 ? b - a : a - b))

    for (const index of ordered) {
        const swapWith = index + step
        if (swapWith < 0 || swapWith >= next.length) {
            continue
        }
        const tmp = next[swapWith]
        next[swapWith] = next[index]
        next[index] = tmp
    }

    return next
}

const applyZOrder = (elements: TemplateElement[], selectedIds: string[], mode: ZOrderMode): TemplateElement[] => {
    if (selectedIds.length === 0) {
        return elements
    }

    const byId = new Map(elements.map((element) => [element.id, element]))
    const selected = selectedIds
        .map((id) => byId.get(id))
        .filter((element): element is TemplateElement => Boolean(element))

    if (selected.length === 0) {
        return elements
    }

    const groupIds = new Set(selected.map((element) => element.groupId ?? null))
    if (groupIds.size !== 1) {
        return elements
    }

    const sharedGroupId = selected[0].groupId ?? null

    if (!sharedGroupId) {
        const topOrder = topLevelOf(elements)
        const idList = topOrder.map((element) => element.id)
        const nextIds = moveIndices(idList, indicesOf(idList, selectedIds), mode)
        const topById = new Map(topOrder.map((element) => [element.id, element]))
        const nextTop = nextIds
            .map((id) => topById.get(id))
            .filter((element): element is TemplateElement => Boolean(element))
        return rebuildElementsByTopOrder(elements, nextTop)
    }

    const group = getGroup(elements, sharedGroupId)
    if (!group) {
        return elements
    }

    return withGroupChildren(
        elements,
        group.id,
        moveIndices(group.children, indicesOf(group.children, selectedIds), mode)
    )
}

export const bringElementsToFront = (elements: TemplateElement[], selectedIds: string[]) =>
    applyZOrder(elements, selectedIds, 'front')

export const sendElementsToBack = (elements: TemplateElement[], selectedIds: string[]) =>
    applyZOrder(elements, selectedIds, 'back')

export const bringElementsForward = (elements: TemplateElement[], selectedIds: string[]) =>
    applyZOrder(elements, selectedIds, 'forward')

export const sendElementsBackward = (elements: TemplateElement[], selectedIds: string[]) =>
    applyZOrder(elements, selectedIds, 'backward')

/** DnD детей внутри одной группы: from/to — индексы в group.children (не visual reverse). */
export const reorderGroupChildren = (
    elements: TemplateElement[],
    groupId: string,
    fromIndex: number,
    toIndex: number
): TemplateElement[] => {
    const group = getGroup(elements, groupId)
    const count = group?.children.length ?? 0
    if (!group || fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= count || toIndex >= count) {
        return elements
    }

    const nextChildren = [...group.children]
    const [moved] = nextChildren.splice(fromIndex, 1)
    nextChildren.splice(toIndex, 0, moved)
    return withGroupChildren(elements, groupId, nextChildren)
}
